// Shared UI translator for the website and the console.
// Dictionaries are keyed by the English text: { langs: ["fr", "es"], entries: { "Save": ["Enregistrer", "Guardar"] } }.
// "{x}" in a key matches any text and is put back in the same order ("Saved {x}." -> "{x} enregistré.").
// Visible text nodes and the placeholder / aria-label / title / alt attributes are swapped in place, so every page is
// covered without wiring each string. Only exact matches change, so member-written text and server Content stay as they
// are. Anything under [data-no-i18n] (Content previews, member text) and form values are never touched.
// A language with no dictionary column (a newly added one) simply shows English.
const ATTRS = ["placeholder", "aria-label", "title", "alt"];
const SKIP = "script,style,textarea,code,pre,[data-no-i18n],[contenteditable='true']";
const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

let cur = { lang: "en", table: null, patterns: [] };
const textSrc = new WeakMap();
const textOut = new WeakMap();
const attrState = new WeakMap(); // element -> { attr: [source, output] }
let observer = null;

export const currentLang = () => cur.lang;
// BCP-47 locale for dates and numbers in the chosen language.
export const currentLocale = () => ({ en: "en-US", fr: "fr-FR", es: "es-ES" }[cur.lang] || cur.lang);

function build(dict, lang) {
  const i = dict?.langs?.indexOf(lang) ?? -1;
  if (i < 0) return { table: null, patterns: [] };
  const table = new Map();
  const patterns = [];
  for (const [en, row] of Object.entries(dict.entries)) {
    if (!row?.[i]) continue;
    if (en.includes("{x}")) patterns.push([new RegExp(`^${en.split("{x}").map(esc).join("(.+?)")}$`, "s"), row[i]]);
    else table.set(en, row[i]);
  }
  return { table, patterns };
}

// Translate one string (also usable directly, e.g. for document.title).
export function tr(s) {
  if (!cur.table || typeof s !== "string") return s;
  const key = s.trim();
  if (!key) return s;
  let t = cur.table.get(key);
  if (t == null) {
    for (const [re, tpl] of cur.patterns) {
      const m = key.match(re);
      if (m) { let k = 1; t = tpl.replace(/\{x\}/g, () => m[k++] ?? ""); break; }
    }
  }
  if (t == null) return s;
  return s.match(/^\s*/)[0] + t + s.match(/\s*$/)[0];
}

const skipped = (el) => !el || !!el.closest?.(SKIP);

function doText(n, relang) {
  const v = n.nodeValue;
  const ours = textOut.get(n) === v && textSrc.has(n);
  if (ours && !relang) return;
  if (skipped(n.parentElement)) return;
  const src = ours ? textSrc.get(n) : v;
  const t = tr(src);
  textSrc.set(n, src);
  textOut.set(n, t);
  if (t !== v) n.nodeValue = t;
}

function doAttr(el, name, relang) {
  const v = el.getAttribute(name);
  if (v == null || el.closest("[data-no-i18n]")) return;
  const st = attrState.get(el) || {};
  const ours = st[name] && st[name][1] === v;
  if (ours && !relang) return;
  const src = ours ? st[name][0] : v;
  const t = tr(src);
  st[name] = [src, t];
  attrState.set(el, st);
  if (t !== v) el.setAttribute(name, t);
}

function walk(root, relang) {
  if (root.nodeType === 3) return doText(root, relang);
  if (root.nodeType !== 1) return undefined;
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = w.nextNode(); n; n = w.nextNode()) doText(n, relang);
  const els = [root, ...root.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(","))];
  for (const el of els) for (const a of ATTRS) if (el.hasAttribute?.(a)) doAttr(el, a, relang);
  return undefined;
}

function start() {
  if (observer || typeof MutationObserver === "undefined") return;
  observer = new MutationObserver((list) => {
    for (const m of list) {
      if (m.type === "characterData") doText(m.target, false);
      else if (m.type === "attributes") doAttr(m.target, m.attributeName, false);
      else m.addedNodes.forEach((n) => walk(n, false));
    }
  });
  observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}

// Page <title>: kept as English source so a later language switch re-translates it.
let titleSrc = null;
const trTitle = (t) => t.split(" · ").map(tr).join(" · ");
export function setDocTitle(t) { titleSrc = t; document.title = trTitle(t); }

// Switch the active dictionary + language and re-translate what's on screen. Also sets <html lang>.
export function setTranslation(lang, dict) {
  cur = { lang: lang || "en", ...build(dict, lang) };
  document.documentElement.lang = cur.lang;
  if (titleSrc) document.title = trTitle(titleSrc);
  start();
  walk(document.body, true);
}
